import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { STAGE_COLORS, STAGE_LABELS } from "../lib/stages";
import { Building2, AlertTriangle, Bed, Home, X, ChevronDown } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import RoomDrawer from "../components/RoomDrawer";
import { useAuth } from "../context/AuthContext";
import { canAccessStage } from "../lib/permissions";
import { useApplication } from "../context/ApplicationContext";
import { EmptyState, MetricCard, PageHeader, ProgressBar } from "../components/OperationalUI";

const DISPLAY_STAGES = [
  "key_return",
  "checkout_inspection",
  "bag_and_bin",
  "maintenance_inspection",
  "internal_repairs",
  "external_repairs",
  "painting",
  "cleaning",
  "cleaning_inspection",
  "welcome_packs",
  "completed",
];

export default function Dashboard() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cleaning, setCleaning] = useState(null);
  const [showOverstayers, setShowOverstayers] = useState(false);
  const [workflowOpen, setWorkflowOpen] = useState(() => {
    const remembered = sessionStorage.getItem("dashboard.workflow-position.open");
    if (remembered !== null) return remembered === "true";
    return false;
  });
  const nav = useNavigate();
  const { user } = useAuth();
  const { effectiveWorkflow, contextRevision } = useApplication();
  const displayStages = (
    effectiveWorkflow?.stages?.filter(stage => stage.enabled && stage.dashboard_visible) ||
    DISPLAY_STAGES.map(key => ({ key, display_name: STAGE_LABELS[key] }))
  ).filter(stage => canAccessStage(user, stage.key));

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [{ data: blocksData }, { data: cleaningData }] = await Promise.all([
          api.get("/dashboard/blocks"),
          api.get("/dashboard/cleaning-stats"),
        ]);
        if (!cancelled) {
          setData(blocksData);
          setCleaning(cleaningData);
        }
      } catch (err) {
        console.error("Dashboard load failed:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [contextRevision]);
  const safeCleaning = cleaning || {};
  const cleaningRooms = safeCleaning.rooms || { completed: 0, total: 0, in_progress: 0 };
  const cleaningCommons = safeCleaning.commons || { completed: 0, total: 0, in_progress: 0 };

  const safeData = Array.isArray(data) ? data : [];
  const totalRooms = safeData.reduce((a, b) => a + (b.total_rooms || 0), 0);
  const totalCommons = safeData.reduce((a, b) => a + (b.total_commons || 0), 0);
  const overstayers = safeData.reduce((a, b) => a + (b.overstayers || 0), 0);
  const workflowTotals = displayStages.map(stage => ({...stage, count: safeData.reduce((sum, block) => sum + (block.counts_rooms?.[stage.key] || 0) + (block.counts_common?.[stage.key] || 0), 0)}));

  return (
    <div data-testid="dashboard-page">
      <PageHeader eyebrow="TARlution · Operations" title="Dashboard" description="Live turnaround readiness across your selected scope." meta={`Updated ${new Date().toLocaleTimeString([], {hour:"2-digit", minute:"2-digit"})}`} />

      <section className="ops-metric-grid" aria-label="Portfolio summary">
        <MetricCard label="Blocks" value={data.length} detail="In selected scope" />
        <MetricCard label="Bedrooms" value={totalRooms} detail="Operational rooms" />
        <MetricCard label="Common areas" value={totalCommons} detail="Shared spaces" />
        <MetricCard
          label="Overstayers"
          value={overstayers}
          tone={overstayers > 0 ? "danger" : "neutral"}
          detail={overstayers > 0 ? "Requires attention" : "No exceptions"}
          onClick={overstayers > 0 ? () => setShowOverstayers(true) : null}
        />
      </section>

      {workflowTotals.length > 0 && <section className="ops-operational-panel" data-testid="dashboard-workflow-position">
        <button type="button" className="flex w-full items-center justify-between gap-4 text-left" aria-expanded={workflowOpen} aria-controls="workflow-position-grid" onClick={() => setWorkflowOpen(value => { sessionStorage.setItem("dashboard.workflow-position.open", String(!value)); return !value; })}>
          <span className="ops-section-title"><span>Current workflow position</span><small>Bedrooms and common areas in the selected scope</small></span>
          <ChevronDown size={20} className={`shrink-0 transition-transform ${workflowOpen ? "rotate-180" : ""}`} aria-hidden="true" />
        </button>
        {workflowOpen && <div id="workflow-position-grid" className="mt-3 grid grid-cols-2 gap-px overflow-hidden border bg-slate-200 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
          {workflowTotals.map(stage => <button key={stage.key} onClick={()=>nav(`/stage/${stage.key}`)} className="bg-white p-3 text-left hover:bg-slate-50" data-testid={`dashboard-stage-${stage.key}`}><span className="block text-[10px] font-bold uppercase tracking-wide text-slate-500">{stage.display_name}</span><strong className="mt-1 block text-2xl">{stage.count}</strong></button>)}
        </div>}
      </section>}

      {cleaning && (
        <div className="ops-progress-panel" data-testid="cleaning-stats">
          <div><div className="ops-section-title"><span>Cleaning progress</span><small>{cleaningRooms.in_progress + cleaningCommons.in_progress} currently in progress</small></div>
          <ProgressBar label={`Bedrooms · ${cleaningRooms.completed} of ${cleaningRooms.total}`} value={cleaningRooms.total ? Math.round(cleaningRooms.completed/cleaningRooms.total*100) : 0}/></div>
          <ProgressBar label={`Common areas · ${cleaningCommons.completed} of ${cleaningCommons.total}`} value={cleaningCommons.total ? Math.round(cleaningCommons.completed/cleaningCommons.total*100) : 0}/>
        </div>
      )}

      {loading ? (
        <div className="text-sm text-gray-500">Loading…</div>
      ) : safeData.length === 0 ? <EmptyState title="No blocks in this scope" description="Choose another Site or ask an administrator to check your location access." /> : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {safeData.map((b) => (
            <BlockCard key={b.block.id} b={b} nav={nav} user={user} displayStages={displayStages} />
          ))}
        </div>
      )}

      {showOverstayers && (
        <OverstayersModal onClose={() => setShowOverstayers(false)} />
      )}
    </div>
  );
}

function OverstayersModal({ onClose }) {
  const [rooms, setRooms] = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/dashboard/overstayers");
        if (!cancelled) setRooms(data);
      } catch (err) {
        console.error("Overstayers load failed:", err);
        if (!cancelled) setRooms([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="fixed inset-0 z-40 flex" data-testid="overstayers-modal">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-full max-w-xl bg-white h-full overflow-y-auto border-l border-gray-200">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-start justify-between z-10">
          <div>
            <div className="text-[11px] font-mono uppercase tracking-wider text-red-700 flex items-center gap-1.5">
              <AlertTriangle size={12} /> Unauthorised overstayers
            </div>
            <h2 className="font-chivo font-black text-2xl mt-1">
              {rooms?.length ?? "…"} room{(rooms?.length ?? 0) === 1 ? "" : "s"}
            </h2>
            <p className="text-xs text-gray-600 mt-1">Contract end has passed and key still marked as Room Occupied.</p>
          </div>
          <button onClick={onClose} data-testid="overstayers-close" className="p-2 hover:bg-gray-100">
            <X size={18} />
          </button>
        </div>
        <div className="p-6">
          {rooms === null ? (
            <div className="text-sm text-gray-500">Loading…</div>
          ) : rooms.length === 0 ? (
            <div className="text-sm text-gray-500">No overstayers right now.</div>
          ) : (
            <div className="border border-gray-200 divide-y divide-gray-100">
              {rooms.map((r) => (
                <button
                  key={r.id}
                  onClick={() => setSelected(r.id)}
                  data-testid={`overstayer-row-${r.id}`}
                  className="w-full text-left px-4 py-3 hover:bg-gray-50 grid grid-cols-[1fr_auto] gap-3 items-center"
                >
                  <div>
                    <div className="font-chivo font-bold text-sm break-words">{r.name}</div>
                    <div className="text-[10px] font-mono uppercase text-gray-500 mt-0.5">
                      {r.block_name} · {r.flat_name}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] font-mono uppercase text-red-700 font-bold">{r.contract_end_date || "—"}</div>
                    <div className="text-[10px] font-mono text-gray-500">{r.key_status}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
        {selected && (
          <RoomDrawer roomId={selected} onClose={() => setSelected(null)} />
        )}
      </div>
    </div>
  );
}

function BlockCard({ b, nav, user, displayStages }) {
  const [view, setView] = useState("rooms"); // rooms | common
  const counts = view === "rooms" ? b.counts_rooms : b.counts_common;

  return (
    <article className="ops-block-card" data-testid={`block-card-${b.block.code}`}>
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-gray-500">
            <Building2 size={14} /> Block / {b.block.code}
          </div>
          <h3 className="font-chivo font-black text-2xl mt-1">{b.block.name}</h3>
        </div>
        <Link
          to={`/blocks?b=${b.block.id}`}
          className="text-xs font-semibold uppercase tracking-wider bg-black text-white px-3 py-1.5 hover:bg-gray-800"
          data-testid={`view-block-${b.block.code}`}
        >
          Open block →
        </Link>
      </div>

      <div className="font-mono text-xs text-gray-500 mb-3 flex items-center gap-2 flex-wrap">
        <span>
          <strong className="text-black">{b.total_rooms}</strong> bedrooms ·{" "}
          <strong className="text-black">{b.total_commons}</strong> common
        </span>
        {b.overstayers > 0 && (
          <span className="inline-flex items-center gap-1 border border-red-300 bg-red-50 text-red-700 px-1.5 py-0.5 font-bold uppercase">
            <AlertTriangle size={10} /> {b.overstayers} overstayer{b.overstayers > 1 ? "s" : ""}
          </span>
        )}
      </div>
      {view === "rooms" && (counts?.cleaning || 0) > 0 && (
        <button
          onClick={() => nav("/stage/cleaning")}
          className="mb-3 text-[10px] font-mono font-bold uppercase border border-red-300 bg-red-50 text-red-700 px-2 py-1"
        >
          Check Cleaning stage for returned rooms and reasons
        </button>
      )}

      <div className="flex border border-gray-200 mb-3" data-testid={`block-view-toggle-${b.block.code}`}>
        <button
          onClick={() => setView("rooms")}
          className={`flex-1 px-3 py-1.5 text-[11px] font-mono font-bold uppercase tracking-wider flex items-center gap-1.5 justify-center ${
            view === "rooms" ? "bg-black text-white" : "bg-white text-gray-700"
          }`}
          data-testid={`block-${b.block.code}-view-rooms`}
        >
          <Bed size={12} /> Bedrooms ({b.total_rooms})
        </button>
        <button
          onClick={() => setView("common")}
          className={`flex-1 px-3 py-1.5 text-[11px] font-mono font-bold uppercase tracking-wider flex items-center gap-1.5 justify-center border-l border-gray-200 ${
            view === "common" ? "bg-black text-white" : "bg-white text-gray-700"
          }`}
          data-testid={`block-${b.block.code}-view-common`}
        >
          <Home size={12} /> Common ({b.total_commons})
        </button>
      </div>

      <div className="grid grid-cols-2 gap-0 border-t border-l border-gray-200">
        {displayStages.map((stage) => {
          const s=stage.key;
          const c = STAGE_COLORS[s] || {bg:"#f8fafc",text:stage.colour||"#475569"};
          const n = counts?.[s] || 0;
          const allowed = canAccessStage(user, s);
          return (
            <button
              key={s}
              onClick={() => nav(`/stage/${s}?type=${view === "rooms" ? "bedroom" : "common"}&block=${b.block.id}`)}
              data-testid={`dash-stage-${b.block.code}-${s}`}
              title={allowed ? undefined : "You do not have access to this stage"}
              className={`border-r border-b border-gray-200 px-3 py-2 flex items-center justify-between text-left transition-colors ${allowed ? "hover:brightness-95" : "opacity-60"}`}
              style={{ backgroundColor: n > 0 ? c.bg : "white" }}
            >
              <span className="text-[11px] font-mono font-bold uppercase tracking-wide" style={{ color: c.text }}>
                {stage.display_name || STAGE_LABELS[s] || "Workflow stage"}
              </span>
              <span
                className="font-chivo font-black text-lg"
                style={{ color: n > 0 ? c.text : "#d1d5db" }}
              >
                {n}
              </span>
            </button>
          );
        })}
      </div>
    </article>
  );
}

function Stat({ label, value, accent, alert, sub, onClick, testId }) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag
      onClick={onClick || undefined}
      data-testid={testId}
      className={`border-r border-gray-200 last:border-r-0 p-5 text-left ${onClick ? "hover:bg-gray-50 cursor-pointer transition-colors" : ""} block w-full`}
    >
      <div className="text-xs font-mono uppercase tracking-wider text-gray-500">
        {label}
      </div>
      <div className={`font-chivo font-black text-3xl md:text-4xl mt-2 ${alert ? "text-red-600" : accent ? "text-blue-700" : "text-black"}`}>
        {value}
      </div>
      {sub && <div className="text-[10px] font-mono text-gray-500 mt-1 uppercase">{sub}</div>}
      {onClick && <div className="text-[10px] font-mono text-gray-500 mt-1 uppercase">Click to view list →</div>}
    </Tag>
  );
}
